/**
 * Пресеты AI-провайдеров для селекта над полем Base URL (задача P1).
 *
 * Идея: «мы просто коннектор» — админ выбирает провайдера из списка, а не вбивает
 * OpenAI-совместимый URL по памяти. Выбор пресета автозаполняет base_url (поле
 * остаётся редактируемым) и показывает модели-подсказки (чипсы, клик — подставить
 * в поле модели). Ключ API пресетом не затрагивается.
 */

export interface AiProviderPreset {
  /** Уникальный id пресета внутри списка (используется как value у <option>). */
  id: string;
  /** Заголовок в селекте. */
  label: string;
  /** Значение, которым заполняется base_url при выборе пресета (см. applyBaseUrl). */
  baseUrl: string;
  /**
   * Заполнять ли base_url значением baseUrl при выборе пресета.
   * false — только у «Свой URL»: поле остаётся как есть (текущее поведение).
   */
  applyBaseUrl: boolean;
  /** Плейсхолдер поля base_url, когда активен этот пресет (если baseUrl пустой). */
  baseUrlPlaceholder?: string;
  /** Короткая пояснительная подсказка под селектом. */
  hint?: string;
  /** Модели-подсказки (чипсы под полем «Модель»). */
  modelHints: string[];
}

const CUSTOM_PRESET: AiProviderPreset = {
  id: 'custom',
  label: 'Свой URL',
  baseUrl: '',
  applyBaseUrl: false,
  modelHints: [],
};

/** Полный список — блок LLM (инференс). */
export const LLM_PROVIDER_PRESETS: AiProviderPreset[] = [
  {
    id: 'ai-factory',
    label: 'AI Factory',
    baseUrl: '',
    applyBaseUrl: true,
    baseUrlPlaceholder: 'https://…/v1 (адрес шлюза AI Factory)',
    hint: 'OpenAI-совместимый шлюз заказчика — укажите фактический адрес.',
    modelHints: [],
  },
  {
    id: 'openai',
    label: 'OpenAI',
    baseUrl: 'https://api.openai.com/v1',
    applyBaseUrl: true,
    modelHints: ['gpt-4o', 'gpt-4.1', 'o3-mini'],
  },
  {
    id: 'anthropic',
    label: 'Anthropic Claude',
    baseUrl: 'https://api.anthropic.com/v1',
    applyBaseUrl: true,
    hint: 'OpenAI-совместимый слой Anthropic.',
    modelHints: ['claude-sonnet-4-5', 'claude-opus-4-1'],
  },
  {
    id: 'gemini',
    label: 'Google Gemini',
    baseUrl: 'https://generativelanguage.googleapis.com/v1beta/openai',
    applyBaseUrl: true,
    modelHints: ['gemini-2.5-pro', 'gemini-2.5-flash'],
  },
  {
    id: 'grok',
    label: 'xAI Grok',
    baseUrl: 'https://api.x.ai/v1',
    applyBaseUrl: true,
    modelHints: ['grok-4', 'grok-3-mini'],
  },
  {
    id: 'local-vllm',
    label: 'Локальный vLLM',
    baseUrl: 'http://vllm:8000/v1',
    applyBaseUrl: true,
    modelHints: [],
  },
  CUSTOM_PRESET,
];

/** Сокращённый список — блоки «Эмбеддинги» и «VLM»: только «Локальный» + «Свой URL». */
export function localOnlyPresets(localBaseUrl: string): AiProviderPreset[] {
  return [
    {
      id: 'local',
      label: 'Локальный',
      baseUrl: localBaseUrl,
      applyBaseUrl: true,
      modelHints: [],
    },
    CUSTOM_PRESET,
  ];
}

/** Определяет активный пресет по сохранённому/текущему base_url (для выбора при загрузке). */
export function resolveActivePresetId(presets: AiProviderPreset[], baseUrl: string): string {
  const normalize = (value: string) => value.trim().replace(/\/+$/, '');
  const normalized = normalize(baseUrl);

  const exactMatch = presets.find((preset) => preset.baseUrl !== '' && normalize(preset.baseUrl) === normalized);
  if (exactMatch) return exactMatch.id;

  if (!normalized) {
    const emptyPreset = presets.find((preset) => preset.applyBaseUrl && preset.baseUrl === '');
    if (emptyPreset) return emptyPreset.id;
  }

  return 'custom';
}
